import { Box, Grid } from '@mui/material'
import { useNavigate } from 'react-router'
import { useRecoilState } from 'recoil'

import CalendarDay from '../../components/calendar/CalendarDay'
import { useCalendarData } from '../../hooks/useCalendarData'
import { sessionState } from '../../state'
import { CalendarWeekProps } from '../../types/ComponentProps'
import {
  filterBookingsForDate,
  hasUserBooking
} from '../../utils/bookingHelpers'
import {
  addDaysToDate,
  getWeekdayName,
  createDayIndicesArray,
  isWeekendDay
} from '../../utils/calendarHelpers'
import { createDateOnly, formatDateToString } from '../../utils/FormatHelpers'

/**
 * CalendarWeek - Container component for managing calendar week display
 *
 * This component uses useCalendarData hook for state management,
 * passes data to CalendarDay components, and handles user actions
 * by coordinating with services.
 *
 * Requirements: 1.1, 1.3, 4.1
 */
export default function CalendarWeek(props: CalendarWeekProps) {
  const navigate = useNavigate()
  const [userState] = useRecoilState(sessionState)

  // Use the calendar data hook for state management
  const [calendarData, calendarActions] = useCalendarData(
    props.startDate,
    props.numOfDays
  )

  /**
   * Handle day click for booking navigation
   */
  const handleDayClick = async (date: Date) => {
    await calendarActions.refreshBookingsForDate(date)
    navigate('/book', { replace: true })
  }

  /**
   * Handle delete booking action
   */
  const handleDeleteClick = async (date: Date) => {
    if (props.deleteBooking) {
      props.deleteBooking(date)
    } else {
      // Fallback to using the calendar service
      await calendarActions.deleteBookingForDate(date)
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={0.5}>
        {createDayIndicesArray(props.numOfDays).map(dayIndex => {
          const startDateOnly = createDateOnly(props.startDate)
          const dayOfWeek = getWeekdayName(startDateOnly, dayIndex)
          const currentDate = addDaysToDate(startDateOnly, dayIndex)
          const isWeekend = isWeekendDay(dayOfWeek)
          const dayBookings = filterBookingsForDate(props.bookings, currentDate)

          // Debug logging for first day
          if (dayIndex === 0) {
            console.log('First day debug:', {
              dayIndex,
              startDate: props.startDate,
              startDateOnly,
              currentDate,
              allBookings: props.bookings?.length || 0,
              dayBookings: dayBookings?.length || 0,
              bookingDates: props.bookings?.map(b => b.forDate) || []
            })
          }
          const hasUserBookingForDay = hasUserBooking(
            dayBookings,
            userState.user?.uid
          )
          const today = new Date()
          const todayDateOnly = createDateOnly(today)
          const isToday =
            formatDateToString(currentDate) === formatDateToString(today)
          const isPast = currentDate < todayDateOnly
          const isHighlighted = userState.highlightedDates?.includes(
            formatDateToString(currentDate)
          )
          // Use local date string to avoid timezone issues
          const year = currentDate.getFullYear()
          const month = String(currentDate.getMonth() + 1).padStart(2, '0')
          const day = String(currentDate.getDate()).padStart(2, '0')
          const dateStr = `${year}-${month}-${day}`
          const dayNotifications = calendarData.notifications[dateStr] || []

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={1.714}
              key={'day' + dayIndex}
              alignItems='stretch'
            >
              <CalendarDay
                date={currentDate}
                dayOfWeek={dayOfWeek}
                isToday={isToday}
                isPast={isPast}
                isWeekend={isWeekend}
                isHighlighted={isHighlighted}
                bookings={dayBookings || []}
                notifications={dayNotifications}
                offices={userState.offices || []}
                hasUserBooking={hasUserBookingForDay}
                canDelete={!!props.deleteBooking}
                onDayClick={handleDayClick}
                onDeleteClick={handleDeleteClick}
              />
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}
